function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

// Cache wishlist counter in localStorage to show the value immediately on page load.
(function () {
  const STORAGE_KEY = 'wishlist_count';
  const counter = document.querySelector('.wkh-counter');

  if (!counter) return;

  const savedCount = localStorage.getItem(STORAGE_KEY);
  if (savedCount !== null) {
    counter.textContent = savedCount;
    counter.closest('.wishlist-icon-count')?.classList.toggle('is-visible', savedCount > 0);
  }

  const observer = new MutationObserver(() => {
    const value = counter.textContent?.trim();

    if (!value || isNaN(value)) return;

    localStorage.setItem(STORAGE_KEY, value);
    counter.closest('.wishlist-icon-count')?.classList.toggle('is-visible', value > 0);
  });

  observer.observe(counter, {
    childList: true,
    characterData: true,
    subtree: true,
  });
})();

//Drawer used for mobile navigation and mini cart
class SideDrawerToggle extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("click", function (evt) {
      evt.preventDefault();

      // Close all drawers
      if (this.getAttribute("data-drawer") == "all") {
        const activeDrawers = document.querySelectorAll("side-drawer.active");
        if (activeDrawers) {
          activeDrawers.forEach((el) => {
            //Toggle drawer event
            const event = new CustomEvent("drawer-toggle", {
              detail: { trigger: this },
            });
            el.dispatchEvent(event);
          });
        }
      } else {
        const drawer = document.getElementById(this.getAttribute("data-drawer"));
        //Toggle drawer event
        const event = new CustomEvent("drawer-toggle", {
          detail: { trigger: this },
        });
        drawer.dispatchEvent(event);
      }
    });
  }
}

customElements.define("side-drawer-toggle", SideDrawerToggle);

class SideDrawer extends HTMLElement {
  constructor() {
    super();
    this._wasOpen = false;

    const thisElement = this;

    this.observer = new MutationObserver(() => {
      if (this._wasOpen && !this.hasAttribute("drawer-open")) {
        thisElement.open(this);
      }
    });

    this.observer.observe(this, {
      attributes: true,
      attributeFilter: ["drawer-open"],
    });

    //Toggle event listeners
    this.addEventListener("drawer-toggle", function (event) {
      if (this.hasAttribute("drawer-open")) {
        thisElement.close(this);
      } else {
        thisElement.open(event);
      }
    });

    //Key up and body click events
    this.addEventListener("keyup", (evt) => evt.code === "Escape" && this.close());
    this.onBodyClick = this.handleBodyClick.bind(this);
  }

  //Open drawer
  open(event) {
    this.openedBy = event?.detail?.trigger || this;
    this._wasOpen = true;
    this.setAttribute("drawer-open", "");
    this.classList.add("active");
    document.body.classList.add("blur");
    document.body.classList.add("drawer--active");

    if (this.classList.contains("drawer--right")) {
      document.body.classList.add("drawer--right-position");
    }

    if (this.classList.contains("drawer--left")) {
      document.body.classList.add("drawer--left-position");
    }

    this.addEventListener(
      "transitionend",
      () => {
        this.focus();
        trapFocus(event.target);
      },
      { once: true }
    );

    document.body.addEventListener("click", this.onBodyClick, {
      passive: true,
    });
  }

  //Close drawer
  close() {
    this._wasOpen = false;
    this.removeAttribute("drawer-open");
    this.classList.remove("active");
    const activeDrawers = document.querySelectorAll(".drawer.active");
    if (activeDrawers.length == 0) {
      document.body.classList.remove("blur");
      document.body.classList.remove("drawer--active");
      document.body.classList.remove("drawer--right-position");
      document.body.classList.remove("drawer--left-position");
    }

    document.body.removeEventListener("click", this.onBodyClick, {
      passive: true,
    });
    removeTrapFocus(this);
  }

  //Body click event to close drawer
  handleBodyClick(evt) {
    const target = evt.target;

    if (!target || !target.isConnected) return;

    const drawerElement = target.closest("side-drawer");
    const isInsideAnyDrawer = drawerElement !== null;
    const isOnToggle = target.closest("side-drawer-toggle") !== null;

    if (!isInsideAnyDrawer && !isOnToggle) {
      this.close();
    }
  }
}

customElements.define("side-drawer", SideDrawer);

function getFocusableElements(container) {
  return Array.from(container.querySelectorAll("summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"));
}

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  elementToFocus.focus();
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

class AccordionCustom extends HTMLElement {
  constructor() {
    super();
    this.details = this.querySelector("details");
    this.summary = this.querySelector("summary");
    this.controller = new AbortController();
  }

  connectedCallback() {
    if (!this.details || !this.summary) return;

    this.setDefaultOpenState();
    this.addEventListeners();
  }

  disconnectedCallback() {
    this.controller.abort();
  }

  addEventListeners() {
    const { signal } = this.controller;

    this.addEventListener("keydown", (e) => this.handleKeyDown(e), { signal });
    this.summary.addEventListener("click", (e) => this.handleClick(e), {
      signal,
    });
    window.matchMedia("(min-width: 990px)").addEventListener("change", () => this.setDefaultOpenState(), { signal });
  }

  handleClick(event) {
    const isMobile = window.innerWidth < 750;
    const isDesktop = !isMobile;

    if ((isMobile && this.dataset.disableOnMobile === "true") || (isDesktop && this.dataset.disableOnDesktop === "true")) {
      event.preventDefault();
    }
  }

  handleKeyDown(event) {
    if (event.key === "Escape" && this.dataset.closeWithEscape === "true") {
      event.preventDefault();
      this.details.open = false;
      this.summary.focus();
    }
  }

  setDefaultOpenState() {
    const isMobile = window.innerWidth < 750;
    this.details.open = (isMobile && this.hasAttribute("open-by-default-on-mobile")) || (!isMobile && this.hasAttribute("open-by-default-on-desktop"));
  }
}

if (!customElements.get("accordion-custom")) {
  customElements.define("accordion-custom", AccordionCustom);
}

class PaginationManager {
  constructor() {
    if (window.__paginationManager) {
      return window.__paginationManager;
    }

    this.loadedPages = new Set();
    this.firstRenderedPage = 1;
    this.lastRenderedPage = 1;
    this.loadedProducts = 0;
    this.totalProducts = 0;
    this.totalPages = 0;
    this.productContainer = document.getElementById("ProductGridContainer");
    this.currentCollection = this.getCollectionIdentifier();
    this.hasManuallyPaginated = false;

    this.isInitialized = false;

    if (!window.__collectionStateListenersBound) {
      this.bindGlobalListeners();
      window.__collectionStateListenersBound = true;
    }

    window.__paginationManager = this;
  }

  getCollectionIdentifier() {
    const match = window.location.pathname.match(/^\/collections\/([^\/?#]+)/);
    return match ? match[1] : "default";
  }

  getPageNumberFromUrl(url) {
    const match = url.match(/[?&]page=(\d+)/);
    return match ? parseInt(match[1]) : 1;
  }

  clearSessionStorage() {
    sessionStorage.removeItem("lastClickedProduct");
    sessionStorage.removeItem("lastClickedProductPage");
  }

  resetIfNeeded() {
    const newCollection = this.getCollectionIdentifier();
    const currentPage = this.getPageNumberFromUrl(window.location.href);

    if (newCollection !== this.currentCollection) {
      this.currentCollection = newCollection;
      this.clearSessionStorage();
      this.resetState();
    } else if (currentPage === 1) {
      this.clearSessionStorage();
    }
  }

  resetState() {
    this.loadedPages = new Set([1]);
    this.firstRenderedPage = 1;
    this.lastRenderedPage = 1;
    this.loadedProducts = 0;
    if (this.productContainer) {
      this.productContainer.innerHTML = "";
    }
    const url = new URL(window.location.href);
    if (url.searchParams.has("page")) {
      url.searchParams.delete("page");
      history.replaceState({ path: url.href }, "", url.href);
    }
  }

  bindGlobalListeners() {
    document.addEventListener("click", (e) => {
      const link = e.target.closest("a[href]");
      if (!link) return;
      const href = link.getAttribute("href");
      if (!href || !href.includes("/collections/")) return;

      const newCollectionMatch = href.match(/^\/collections\/([^\/?#]+)/);
      if (!newCollectionMatch) return;

      const newCollection = newCollectionMatch[1];
      const current = this.getCollectionIdentifier();

      if (newCollection !== current) {
        this.clearSessionStorage();
      }
    });

    const originalPush = history.pushState;
    const originalReplace = history.replaceState;

    const check = () => {
      const mgr = window.__paginationManager;
      if (mgr) mgr.resetIfNeeded();
    };

    history.pushState = function (...args) {
      originalPush.apply(this, args);
      check();
    };

    history.replaceState = function (...args) {
      originalReplace.apply(this, args);
      check();
    };

    window.addEventListener("popstate", check);
  }

  resetFromHTML(htmlString) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");

    const loadButton = doc.querySelector("loading-button button");
    if (!loadButton) {
      this.totalProducts = doc.querySelectorAll("#ProductGridContainer .collection-product").length;
      this.totalPages = 1;
      this.loadedProducts = this.totalProducts;
      this.loadedPages = new Set([1]);
      this.firstRenderedPage = 1;
      this.lastRenderedPage = 1;
      this.hasManuallyPaginated = false;
      this.clearSessionStorage();
      return;
    }

    this.totalProducts = parseInt(loadButton.getAttribute("data-total-results-count")) || 0;
    this.totalPages = parseInt(loadButton.getAttribute("data-total-pages")) || 1;
    this.loadedProducts = parseInt(loadButton.getAttribute("data-loaded-products-total")) || this.totalProducts;

    const currentPage = this.getPageNumberFromUrl(window.location.href);
    this.loadedPages = new Set([currentPage]);
    this.firstRenderedPage = currentPage;
    this.lastRenderedPage = currentPage;
    this.hasManuallyPaginated = false;
    this.clearSessionStorage();
  }
}

window.__paginationManager = window.__paginationManager || new PaginationManager();

class LoadingButton extends HTMLElement {
  constructor() {
    super();

    this.manager = window.__paginationManager;

    // Overlay
    this.overlay = document.getElementById("loading-overlay");
    if (!this.overlay) {
      this.overlay = document.createElement("div");
      this.overlay.id = "loading-overlay";
      this.overlay.innerHTML = '<div class="spinner"></div>';
      document.body.appendChild(this.overlay);
    }

    // Elements
    this.loadButton = this.querySelector("button");
    this.progressBar = this.querySelector(".progress-bar");
    this.resultCount = this.querySelector(".result-count");
    this.automaticLoading = this.getAttribute("automatic-loading");
    this.loadButtonContainer = this.closest(".pagination-wrapper");
    this.productContainer = document.getElementById("ProductGridContainer");
    this.vanillaPagination = document.getElementById("pagination");

    if (!this.manager.isInitialized && this.loadButton) {
      this.manager.totalProducts = parseInt(this.loadButton.getAttribute("data-total-results-count")) || 0;
      this.manager.totalPages = parseInt(this.loadButton.getAttribute("data-total-pages")) || 0;
      this.manager.loadedProducts = parseInt(this.loadButton.getAttribute("data-loaded-products-total")) || 0;

      const currentPage = this.manager.getPageNumberFromUrl(window.location.href);
      this.manager.loadedPages = new Set([currentPage]);
      this.manager.firstRenderedPage = currentPage;
      this.manager.lastRenderedPage = currentPage;
      this.manager.isInitialized = true;

      this.manager.resetIfNeeded();
    }

    this.loadButton.addEventListener("click", this.loadMore.bind(this));

    if (this.automaticLoading === "true") {
      this.observer = new IntersectionObserver(this.handleIntersection.bind(this), {
        root: null,
        rootMargin: "0px",
        threshold: 0.25,
      });
      this.observer.observe(this.loadButton);
    }

    if (!window.__productClickBound) {
      document.addEventListener("click", (e) => {
        const link = e.target.closest("product-card a");
        if (!link) return;
        const product = link.closest("product-card");
        if (!product) return;

        const handle = product.dataset.productHandle;
        const page = parseInt(product.dataset.productPage) || 1;

        sessionStorage.setItem("lastClickedProduct", handle);
        sessionStorage.setItem("lastClickedProductPage", page);
      });
      window.__productClickBound = true;
    }

    if (!window.__restoreBound) {
      window.addEventListener("DOMContentLoaded", () => this.restoreLastClickedProduct());
      window.__restoreBound = true;
    }

    this.updateUI();
  }

  async restoreLastClickedProduct() {
    if (this.manager.hasManuallyPaginated) return;

    const handle = sessionStorage.getItem("lastClickedProduct");
    const page = parseInt(sessionStorage.getItem("lastClickedProductPage"));

    if (!handle || !page) return;

    const savedLayout = localStorage.getItem("layout-grid-option");
    const isSmallExpected = savedLayout === "grid--small";

    if (isSmallExpected) {
      await new Promise((resolve) => {
        const onGridReady = () => {
          document.removeEventListener("gridLayoutChanged", onGridReady);
          setTimeout(resolve, 50);
        };
        document.addEventListener("gridLayoutChanged", onGridReady);

        setTimeout(() => {
          document.removeEventListener("gridLayoutChanged", onGridReady);
          resolve();
        }, 300);
      });
    }

    this.showOverlay();
    await this.goToProduct(handle, page);
    this.hideOverlay();

    sessionStorage.removeItem("lastClickedProduct");
    sessionStorage.removeItem("lastClickedProductPage");
  }

  async goToProduct(handle, targetPage) {
    const url = new URL(window.location.href);
    url.searchParams.set("page", targetPage);
    history.replaceState({ path: url.href }, "", url.href);

    await this.fetchPage(url.href, "replace");

    const product = this.productContainer?.querySelector(`product-card[data-product-handle="${handle}"]`);
    if (product) {
      product.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  async fetchPage(url, mode = "append") {
    const pageNum = this.manager.getPageNumberFromUrl(url);
    if (this.manager.loadedPages.has(pageNum)) return;

    try {
      const res = await fetch(url);
      const html = await res.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const newGrid = doc.getElementById("ProductGridContainer");

      if (!newGrid || !this.productContainer) return;

      const newHTML = newGrid.innerHTML;

      if (mode === "replace") {
        this.productContainer.innerHTML = newHTML;
        this.manager.loadedPages = new Set([pageNum]);
        this.manager.firstRenderedPage = pageNum;
        this.manager.lastRenderedPage = pageNum;
      } else {
        const btnType = this.loadButton.dataset.buttonType;
        if (btnType === "load-more") {
          this.productContainer.insertAdjacentHTML("beforeend", newHTML);
          this.manager.lastRenderedPage = Math.max(this.manager.lastRenderedPage, pageNum);
        } else {
          const heightBefore = this.productContainer.scrollHeight;

          this.productContainer.insertAdjacentHTML("afterbegin", newHTML);
          this.manager.firstRenderedPage = Math.min(this.manager.firstRenderedPage, pageNum);

          const heightAfter = this.productContainer.scrollHeight;
          const addedHeight = heightAfter - heightBefore;

          if (addedHeight > 0) {
            window.scrollBy({
              top: addedHeight,
              behavior: "instant"
            });        
           }
        }
        this.manager.loadedPages.add(pageNum);
      }

      this.manager.loadedProducts = this.productContainer.querySelectorAll(".collection-product").length;

      if (this.manager.firstRenderedPage === 1) {
        this.manager.clearSessionStorage();
      }

      const newNav = doc.getElementById("pagination");
      if (newNav && this.vanillaPagination) {
        this.vanillaPagination.innerHTML = newNav.innerHTML;
      }

      document.querySelectorAll("loading-button").forEach(el => el.updateUI());
      document.dispatchEvent(new Event("mobile-gallery:refresh"));
    } catch (err) {
      console.error("Fetch page failed", err);
    }
  }

  loadMore() {
    sessionStorage.removeItem("lastClickedProduct");
    sessionStorage.removeItem("lastClickedProductPage");
    this.manager.hasManuallyPaginated = true;

    const btnType = this.loadButton.dataset.buttonType;
    let targetPage;

    if (btnType === "load-more") {
      targetPage = this.manager.lastRenderedPage + 1;
      if (targetPage > this.manager.totalPages) return;
    } else {
      targetPage = this.manager.firstRenderedPage - 1;
      if (targetPage < 1) return;
    }

    const url = new URL(window.location.href);
    url.searchParams.set("page", targetPage);
    this.manager.url = url.href;

    history.pushState({ path: url.href }, "", url.href);

    this.loadButton.classList.add("loading");
    const spinner = this.loadButton.querySelector(".loading-overlay__spinner");
    if (spinner) spinner.classList.remove("hidden");

    this.fetchPage(url.href, "append");
  }

  handleIntersection(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        this.loadMore();
      }
    });
  }

  updateUI() {
    const { firstRenderedPage, lastRenderedPage, totalPages, loadedProducts, totalProducts } = this.manager;

    const loadMoreWrapper = this.loadButtonContainer?.querySelector('[data-button-type="load-more"]')?.closest(".pagination-wrapper");
    const loadPrevWrapper = this.loadButtonContainer?.querySelector('[data-button-type="load-previous"]')?.closest(".pagination-wrapper");

    if (loadMoreWrapper) loadMoreWrapper.classList.toggle("hidden", lastRenderedPage >= totalPages);
    if (loadPrevWrapper) loadPrevWrapper.classList.toggle("hidden", firstRenderedPage <= 1);

    [this.loadButton].forEach(btn => {
      if (btn) {
        btn.classList.remove("loading");
        const spinner = btn.querySelector(".loading-overlay__spinner");
        if (spinner) spinner.classList.add("hidden");
      }
    });

    if (this.progressBar) {
      const progress = totalProducts ? (loadedProducts / totalProducts) * 100 : 0;
      this.progressBar.style.setProperty("--progress", `${progress}%`);
    }
    if (this.resultCount) {
      this.resultCount.textContent = `Showing ${loadedProducts} of ${totalProducts}`;
    }
  }

  showOverlay() {
    this.overlay.classList.add("active");
  }

  hideOverlay() {
    this.overlay.classList.remove("active");
  }
}

customElements.define("loading-button", LoadingButton);

document.addEventListener("DOMContentLoaded", function () {
  function cleanPaginationURL() {
    const currentURL = new URL(window.location);
    if (currentURL.searchParams.has("phcursor")) {
      currentURL.searchParams.delete("phcursor");
      const cleanURL = currentURL.toString();
      if (cleanURL !== window.location.href) {
        history.replaceState({}, "", cleanURL);
      }
    }
  }

  const loadMoreButtons = document.querySelectorAll('loading-button button[rel="nofollow"]');
  loadMoreButtons.forEach((button) => {
    button.addEventListener("click", function () {
      setTimeout(cleanPaginationURL, 1000);
    });
  });

  cleanPaginationURL();
});

document.addEventListener("DOMContentLoaded", function () {
  function cleanPaginationURL() {
    const currentURL = new URL(window.location);
    if (currentURL.searchParams.has("phcursor")) {
      currentURL.searchParams.delete("phcursor");
      const cleanURL = currentURL.toString();
      if (cleanURL !== window.location.href) {
        history.replaceState({}, "", cleanURL);
      }
    }
  }

  const loadMoreButtons = document.querySelectorAll('loading-button button[rel="nofollow"]');
  loadMoreButtons.forEach((button) => {
    button.addEventListener("click", function () {
      setTimeout(cleanPaginationURL, 1000);
    });
  });

  cleanPaginationURL();
});

class ProgressBar extends HTMLElement {
  constructor() {
    super();

    this.progress = parseInt(this.dataset.value);
  }
}

customElements.define("progress-bar", ProgressBar);

window.customElements.define(
  "layout-toggle",
  class GridToggle extends HTMLElement {
    constructor() {
      super();

      this.largeBtn = this.querySelector("#largeGridViewButton");
      this.mediumBtn = this.querySelector("#mediumGridViewButton");
      this.smallBtn = this.querySelector("#smallGridViewButton");

      this.buttons = [this.largeBtn, this.mediumBtn, this.smallBtn].filter(Boolean);

      if (!this.buttons.length) return;

      this.largeBtn?.addEventListener("click", () => this.setGrid("large"));
      this.mediumBtn?.addEventListener("click", () => this.setGrid("medium"));
      this.smallBtn?.addEventListener("click", () => this.setGrid("small"));

      window.addEventListener("DOMContentLoaded", () => {
        const saved = localStorage.getItem("layout-grid-option");

        if (saved === "grid--small") {
          this.setGrid("small", { restore: true });
          this.maybeAutoLoadPage2();
        } else if (saved === "grid--medium") {
          this.setGrid("medium", { restore: true });
        } else {
          this.setGrid("large", { restore: true });
        }
      });
    }

    setGrid(size, { restore = false } = {}) {
      const grid = document.getElementById("ProductGridContainer");
      if (!grid) return;

      grid.classList.remove("grid--large", "grid--medium", "grid--small");
      grid.classList.add(`grid--${size}`);

      this.buttons.forEach((btn) => {
        const isActive = btn.id === `${size}GridViewButton`;

        btn.classList.toggle("active", isActive);
        btn.setAttribute("aria-pressed", isActive ? "true" : "false");
      });

      localStorage.setItem("layout-grid-option", `grid--${size}`);

      if (!restore && size === "small") {
        this.maybeAutoLoadPage2();
      }
    }

    maybeAutoLoadPage2() {
      if (this.hasAutoLoadedPage2) return;

      const manager = window.__paginationManager;
      if (!manager) return;

      const currentPage = manager.getPageNumberFromUrl(window.location.href);
      if (currentPage !== 1) return;
      if (manager.lastRenderedPage > 1) return;
      if (manager.totalPages <= 1) return;

      this.hasAutoLoadedPage2 = true;

      sessionStorage.removeItem("lastClickedProduct");
      sessionStorage.removeItem("lastClickedProductPage");

      manager.hasManuallyPaginated = true;

      const loadMoreBtn = document.querySelector(
        'loading-button button[data-button-type="load-more"]'
      );

      if (loadMoreBtn) {
        loadMoreBtn.closest("loading-button").loadMore();
      }
    }
  }
);

window.customElements.define(
  "image-toggle",
  class ImageToggle extends HTMLElement {
    constructor() {
      super();

      this.modelBtn = this.querySelector("#modelViewButton");
      this.productBtn = this.querySelector("#productViewButton");

      this.buttons = [this.modelBtn, this.productBtn].filter(Boolean);

      if (!this.buttons.length) return;

      this.modelBtn?.addEventListener("click", () => this.setView("model"));
      this.productBtn?.addEventListener("click", () => this.setView("product"));

      window.addEventListener("DOMContentLoaded", () => {
        const saved = localStorage.getItem("layout-image-option");

        if (saved === "grid--model") {
          this.setView("model", { restore: true });
        } else {
          // default
          this.setView("product", { restore: true });
        }
      });
    }

    setView(type, { restore = false } = {}) {
      const grid = document.getElementById("ProductGridContainer");
      if (!grid) return;

      grid.classList.remove("grid--model", "grid--product");
      grid.classList.add(`grid--${type}`);

      this.buttons.forEach((btn) => {
        const isActive = btn.id === `${type}ViewButton`;

        btn.classList.toggle("active", isActive);
        btn.setAttribute("aria-pressed", isActive ? "true" : "false");
      });

      localStorage.setItem("layout-image-option", `grid--${type}`);
    }
  }
);

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector("form");
    facetForm.addEventListener("input", this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector("#FacetsWrapperDesktop");
    if (facetWrapper) facetWrapper.addEventListener("keyup", onKeyUpEscape);

    //this.changeCountPosition();
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener("popstate", onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll(".js-facet-remove").forEach((element) => {
      element.classList.toggle("disabled", disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById("ProductCount");
    const countContainerDesktop = document.getElementById("ProductCountDesktop");
    document.getElementById("ProductGridContainer").classList.add("loading");
    if (countContainer) {
      countContainer.classList.add("loading");
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add("loading");
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl) ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event) : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
  }

  static renderProductGridContainer(html) {
    const container = document.getElementById("ProductGridContainer");
    container.innerHTML = new DOMParser().parseFromString(html, "text/html").getElementById("ProductGridContainer").innerHTML;

    const manager = window.__paginationManager;
    if (manager) {
      manager.resetFromHTML(html);
      document.querySelectorAll("loading-button").forEach(el => el.updateUI());
    }
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, "text/html").getElementById("ProductCount").innerHTML;
    const container = document.getElementById("ProductCount");

    const containerDesktop = document.getElementById("ProductCountDesktop");
    container.innerHTML = count;

    const filterToggleEl = new DOMParser().parseFromString(html, "text/html").querySelector(".mobile-filter-toggle");
    const filterToggle = document.querySelector(".mobile-filter-toggle");
    if (filterToggleEl && filterToggle) {
      filterToggle.classList = filterToggleEl.classList;
      document.dispatchEvent(new Event("mobile-gallery:refresh"));
    }

    container.classList.remove("loading");
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove("loading");
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");

    const facetDetailsElements = parsedHTML.querySelectorAll("#FacetFiltersForm .js-filter, #FacetFiltersFormFilter .js-filter, #FacetFiltersPillsForm .js-filter");
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest(".js-filter") : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter((element) => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    //if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, event.target.closest(".js-filter"));
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [".active-facets-mobile", ".active-facets-desktop"];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = [".filter-facets__open", ".filter-facets__count", ".filter-facets__sort", ".drawer__footer .filter-facets__footer"];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    //document.getElementById("FacetFiltersFormFilter").closest("side-drawer").bindEvents();
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector(".facets__selected");
    const sourceElement = source.querySelector(".facets__selected");

    const targetElementAccessibility = target.querySelector(".facets__summary");
    const sourceElementAccessibility = source.querySelector(".facets__summary");

    if (sourceElement && targetElement) {
      target.querySelector(".facets__selected").outerHTML = source.querySelector(".facets__selected").outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector(".facets__summary").outerHTML = source.querySelector(".facets__summary").outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, "", `${window.location.pathname}${searchParams && "?".concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById("ProductGridContainer").dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll("facet-filters-form form");
    if (event.srcElement.className == "mobile-facets__checkbox") {
      const searchParams = this.createSearchParams(event.target.closest("form"));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest("form").id === "FacetFiltersFormFilter";

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === "FacetSortForm" || form.id === "FacetFiltersForm" || form.id === "FacetSortDrawerForm") {
            const noJsElements = document.querySelectorAll(".no-js-list");
            noJsElements.forEach((el) => el.remove());
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === "FacetFiltersFormFilter") {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join("&"), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf("?") == -1 ? "" : event.currentTarget.href.slice(event.currentTarget.href.indexOf("?") + 1);
    FacetFiltersForm.renderPage(url);
  }

  changeCountPosition() {
    const container = document.getElementById("ProductCount");

    if (container && container.closest(".hidden")) {
      const collectionTitle = document.querySelector("h1");

      if (collectionTitle) {
        const isMobile = window.innerWidth < 900;
        if (isMobile && !container.parentElement.isSameNode(collectionTitle)) {
          collectionTitle.insertAdjacentElement("afterbegin", container);
        }
      }
    }
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll("input").forEach((element) => element.addEventListener("change", this.onRangeChange.bind(this)));
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll("input");
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute("max", maxInput.value);
    if (minInput.value) maxInput.setAttribute("min", minInput.value);
    if (minInput.value === "") maxInput.setAttribute("min", 0);
    if (maxInput.value === "") minInput.setAttribute("max", maxInput.getAttribute("max"));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute("min"));
    const max = Number(input.getAttribute("max"));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define("price-range", PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector("a");
    facetLink.setAttribute("role", "button");
    facetLink.addEventListener("click", this.closeFilter.bind(this));
    facetLink.addEventListener("keyup", (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === "SPACE") this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest("facet-filters-form") || document.querySelector("facet-filters-form");
    form.onActiveFilterClick(event);
  }
}

customElements.define("facet-remove", FacetRemove);

class ScrollableBlock extends HTMLElement {
  constructor() {
    super();

    this.wrapper = this.querySelector(".scrollable-wrapper");
    this.scrollable = this.wrapper;

    this.prevBtn = document.createElement("button");
    this.nextBtn = document.createElement("button");
    this.prevBtn.className = "scroll-btn prev-btn";
    this.nextBtn.className = "scroll-btn next-btn";

    this.prevBtn.hidden = true;
    this.nextBtn.hidden = true;

    this.prepend(this.prevBtn);
    this.append(this.nextBtn);

    this.setupResponsiveMode();
    this.setupEvents();

    requestAnimationFrame(() => this.checkScrollState());
  }

  setupResponsiveMode() {
    const mq = window.matchMedia("(max-width: 750px)");
    const hasVerticalClass = this.classList.contains("desktop-vertical");

    const updateMode = () => {
      if (hasVerticalClass) {
        if (!mq.matches) {
          this.setAttribute("vertical", "");
        }
      }

      this.isVertical = this.hasAttribute("vertical");
      this.prevBtn.innerHTML = this.isVertical ? "↑" : "←";
      this.nextBtn.innerHTML = this.isVertical ? "↓" : "→";
      this.checkScrollState();
    };

    updateMode();
    mq.addEventListener("change", updateMode);
  }

  setupEvents() {
    this.prevBtn.addEventListener("click", () => this.scrollBy(-1));
    this.nextBtn.addEventListener("click", () => this.scrollBy(1));

    this.scrollable.addEventListener("scroll", () => this.checkScrollState());

    const observer = new ResizeObserver(() => this.checkScrollState());
    observer.observe(this.scrollable);
    observer.observe(this);
  }

  scrollBy(direction) {
    const amount = (this.isVertical ? this.scrollable.clientHeight : this.scrollable.clientWidth) * 0.4;

    this.scrollable.scrollBy({
      left: this.isVertical ? 0 : amount * direction,
      top: this.isVertical ? amount * direction : 0,
      behavior: "smooth",
    });
  }

  checkScrollState() {
    requestAnimationFrame(() => {
      if (this.isVertical) {
        const { scrollHeight, clientHeight, scrollTop } = this.scrollable;
        const isScrollable = scrollHeight > clientHeight + 1;
        const tolerance = 5;

        this.prevBtn.hidden = !isScrollable || scrollTop <= tolerance;
        this.nextBtn.hidden = !isScrollable || scrollTop >= scrollHeight - clientHeight - tolerance;
      } else {
        const { scrollWidth, clientWidth, scrollLeft } = this.scrollable;
        const isScrollable = scrollWidth > clientWidth + 1;
        const tolerance = 5;

        this.prevBtn.hidden = !isScrollable || scrollLeft <= tolerance;
        this.nextBtn.hidden = !isScrollable || scrollLeft >= scrollWidth - clientWidth - tolerance;
      }
    });
  }
}

customElements.define("scrollable-block", ScrollableBlock);

// Scrollable Header
document.addEventListener("DOMContentLoaded", () => {
  const headerWrapper = document.querySelector(".header-wrapper");
  const header = headerWrapper.querySelector("header");
  const licalizationSelector = headerWrapper.querySelector("localization-form");
  const initScrollY = window.scrollY;
  if (!header) return;

  let lastScrollY = 0;
  let ticking = false;

  if (initScrollY > 0) {
    headerWrapper.classList.add("header--scrollable");
  }

  function updateHeader() {
    const currentScrollY = window.scrollY;

    if (currentScrollY > 0) {
      headerWrapper.classList.add("header--scrollable");
    } else {
      headerWrapper.classList.remove("header--scrollable");
    }

    if (currentScrollY > lastScrollY && currentScrollY > 100) {
      headerWrapper.classList.add("header--hidden");
      if (licalizationSelector) licalizationSelector.hidePanel();
    } else {
      headerWrapper.classList.remove("header--hidden");
    }

    lastScrollY = currentScrollY;
    ticking = false;
  }

  const heroBanner = document.querySelector("main > .hero-banner:first-child .media-content-background");

  if (!heroBanner) {
    headerWrapper.classList.remove("transparent-header");
  }

  function onScroll() {
    if (!ticking) {
      window.requestAnimationFrame(updateHeader);
      ticking = true;
    }
  }

  window.addEventListener("scroll", onScroll);
});

class RTEFormatter extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll("table").forEach(this.#formatTable);
    this.querySelectorAll("img").forEach(this.#formatImg);
  }

  /**
   * Formats a table for easier styling
   * @param {HTMLTableElement} table
   */
  #formatTable(table) {
    const wrapper = document.createElement("div");
    wrapper.classList.add("rte-table-wrapper");
    const parent = table.parentNode;
    if (parent) {
      parent.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    }
  }

  #formatImg(img) {
    const wrapper = document.createElement("div");
    const parent = img.closest("p");
    if (parent) parent.classList.add("rte-img-wrapper");
  }
}

if (!customElements.get("rte-formatter")) {
  customElements.define("rte-formatter", RTEFormatter);
}

// Accordions init
document.addEventListener("DOMContentLoaded", function () {
  initAccordions();
  window.addEventListener("resize", syncOpenHeights, { passive: true });
});

document.addEventListener("product:refresh", function () {
  initAccordions();
});

function initAccordions() {
  const allAccordions = document.querySelectorAll(".accordion");
  allAccordions.forEach(initAccordion);
}

document.addEventListener("accordion:init", (event) => {
  const accordion = event.detail?.accordion;
  if (accordion) {
    initAccordion(accordion);
  }
});

function initAccordion(accordionEl) {
  if (accordionEl.hasAttribute("data-accordion-initialized")) return;

  const header = accordionEl.querySelector(".accordion__header");
  const content = accordionEl.querySelector(".accordion__content");
  const icon = accordionEl.querySelector(".accordion__icon");
  const shouldOpen = accordionEl.dataset.open === "true";

  if (shouldOpen) {
    content.style.maxHeight = content.scrollHeight + "px";
    header.setAttribute("aria-expanded", "true");
    if (icon) icon.classList.add("is-open");
  } else {
    content.style.maxHeight = 0;
    header.setAttribute("aria-expanded", "false");
    if (icon) icon.classList.remove("is-open");
  }

  header.addEventListener("click", function () {
    const isOpen = header.getAttribute("aria-expanded") === "true";
    if (isOpen) {
      content.style.maxHeight = 0;
      if (icon) icon.classList.remove("is-open");
      header.setAttribute("aria-expanded", "false");
    } else {
      content.style.maxHeight = content.scrollHeight + "px";
      if (icon) icon.classList.add("is-open");
      header.setAttribute("aria-expanded", "true");
    }
  });

  accordionEl.setAttribute("data-accordion-initialized", "true");
}

function syncOpenHeights() {
  document.querySelectorAll('.accordion .accordion__header[aria-expanded="true"]').forEach((header) => {
    const content = header.parentElement.querySelector(".accordion__content");
    if (content) content.style.maxHeight = content.scrollHeight + "px";
  });
}

//Init mobile featured blocks Sliders
document.addEventListener("DOMContentLoaded", function () {
  const sliders = document.querySelectorAll(".mobile-featured-blocks-slider");
  const PROGRESS_BAR_SELECTOR = ".menu-featured-blocks-progress";
  const instances = new Map();
  if (!sliders.length) return;

  sliders.forEach((splideEl) => {
    if (!splideEl || splideEl.dataset.mounted === "1") return;

    const carousel = new Splide(splideEl, {
      perPage: 2,
      perMove: 1,
      pagination: false,
      arrows: false,
      speed: 600,
      autoplay: true,
      interval: 3000,
      pauseOnHover: true,
      type: "loop",
      breakpoints: {
        450: { perPage: 1 },
      },
    });
    const redraw = () => updateProgress(splideEl, carousel);
    carousel.on("mounted move updated refresh", redraw);

    carousel.mount();
    instances.set(splideEl, carousel);
    redraw();
    splideEl.dataset.mounted = "1";
  });

  function resolveProgressBar(el) {
    const parent = el.parentElement;
    let bar = parent ? parent.querySelector(PROGRESS_BAR_SELECTOR) : null;
    if (!bar) bar = el.querySelector(PROGRESS_BAR_SELECTOR);
    return bar || null;
  }

  function updateProgress(el, splide) {
    const bar = resolveProgressBar(el);
    if (!bar) return;
    const end = splide.Components.Controller.getEnd() + 1;
    const rate = Math.min((splide.index + 1) / end, 1);
    bar.style.width = `${rate * 100}%`;
  }
});

// Featured Product Card
class FeaturedProduct extends HTMLElement {
  constructor() {
    super();
    this.productCard = this.querySelector("product-card");
    this.media = this.querySelector(".product-card__media-image");
    this.setupEvents();
  }

  setupEvents() {
    if (!this.productCard || !this.media) return;

    this.media.addEventListener("click", (event) => {
      if (!this.productCard.classList.contains("active")) {
        event.preventDefault();
        event.stopPropagation();
        this.productCard.classList.add("active");
      } else {
        window.location = this.media.href;
      }
    });

    document.addEventListener("click", (event) => {
      if (!this.contains(event.target)) {
        this.productCard.classList.remove("active");
      }
    });
  }
}

customElements.define("featured-product", FeaturedProduct);
